import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, User, Mail, ShoppingCart, AlertCircle } from 'lucide-react';
import { ordersAPI, referralStore } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { toast } from 'sonner';

export default function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { format, currency } = useCurrency();
  const { event, items } = location.state || {};

  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!event || !items) {
    navigate('/explore');
    return null;
  }

  const ticketMap = {};
  (event.ticket_types || []).forEach(tt => { ticketMap[tt.type_id] = tt; });

  const cartItems = items.map(item => ({
    ...item,
    ticket: ticketMap[item.ticket_type_id],
  })).filter(i => i.ticket);

  const totalPrice = cartItems.reduce((sum, i) => sum + i.ticket.price * i.quantity, 0);

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!user && (!guestName || !guestEmail)) {
      setError('Please enter your name and email to continue');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp = await ordersAPI.create({
        eventId: event.event_id,
        items: cartItems.map(i => ({ ticket_type_id: i.ticket_type_id, quantity: i.quantity })),
        guestEmail: !user ? guestEmail : undefined,
        guestName: !user ? guestName : undefined,
        currency: currency,
        hostUrl: window.location.origin,
        referralCode: referralStore.get(event.event_id),
      });
      const { checkout_url } = resp.data;
      window.location.href = checkout_url;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Payment initialization failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-16 page-enter" data-testid="checkout-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-6 transition-colors">
          <ArrowLeft size={16} /> {t('back')}
        </button>

        <h1 className="text-3xl font-black uppercase tracking-tight text-white mb-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          {t('checkout')}
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Order summary */}
          <div>
            <div className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-6 mb-6">
              <h2 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-4">Order Summary</h2>
              <div className="mb-4">
                <p className="text-white font-black text-lg uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>{event.title}</p>
                <p className="text-gray-400 text-sm mt-1">{event.date} • {event.location}</p>
              </div>
              <div className="space-y-3 border-t border-gray-800 pt-4">
                {cartItems.map(item => (
                  <div key={item.ticket_type_id} className="flex justify-between items-center" data-testid={`cart-item-${item.ticket_type_id}`}>
                    <div>
                      <p className="text-white text-sm font-medium">{item.ticket.name}</p>
                      <p className="text-gray-500 text-xs">x{item.quantity}</p>
                    </div>
                    <p className="text-white font-bold text-sm">{format(item.ticket.price * item.quantity)}</p>
                  </div>
                ))}
                <div className="border-t border-gray-800 pt-3 flex justify-between items-center">
                  <span className="text-white font-bold uppercase text-sm">Total</span>
                  <span className="text-white font-black text-xl" style={{ color: '#E63946' }} data-testid="checkout-total">{format(totalPrice)}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 text-gray-500 text-xs bg-[#0A0A0A] border border-gray-800 rounded-lg p-3">
              <CreditCard size={14} className="text-green-400" />
              <span>Secured by Stripe. Your payment info is never stored on our servers.</span>
            </div>
          </div>

          {/* Checkout form */}
          <div>
            <form onSubmit={handlePayment} className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-6">
              <h2 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-6">Your Details</h2>

              {user ? (
                <div className="flex items-center gap-3 bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-6">
                  {user.picture && <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />}
                  <div>
                    <p className="text-white font-semibold text-sm">{user.name}</p>
                    <p className="text-gray-400 text-xs">{user.email}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 mb-6">
                  <p className="text-gray-400 text-xs uppercase font-bold tracking-wider">{t('guestCheckout')}</p>
                  <div>
                    <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider mb-1.5">{t('fullName')}</label>
                    <div className="relative">
                      <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input
                        type="text"
                        value={guestName}
                        onChange={e => setGuestName(e.target.value)}
                        required
                        className="w-full bg-[#111] border border-gray-700 text-white pl-9 pr-3 py-2.5 rounded text-sm"
                        placeholder="Enter your full name"
                        data-testid="guest-name-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider mb-1.5">{t('email')}</label>
                    <div className="relative">
                      <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input
                        type="email"
                        value={guestEmail}
                        onChange={e => setGuestEmail(e.target.value)}
                        required
                        className="w-full bg-[#111] border border-gray-700 text-white pl-9 pr-3 py-2.5 rounded text-sm"
                        placeholder="Your email (for ticket delivery)"
                        data-testid="guest-email-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg p-3 mb-4">
                  <AlertCircle size={14} />
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3.5 rounded text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="pay-btn"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <CreditCard size={16} />
                    {t('proceedToPayment')} • {format(totalPrice)}
                  </>
                )}
              </button>

              <p className="text-gray-600 text-xs text-center mt-3">
                You'll be redirected to Stripe to complete payment
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
